// Primitivas de criptografia para o cadastro/login por estação de trabalho.
//
// Cada navegador gera um par de chaves ECDSA P-256: a pública vai para o
// backend (associada ao usuário após aprovação do supervisor); a privada
// nunca sai do navegador e prova posse assinando um nonce a cada login.
//
// Os formatos abaixo (base64url sem padding, JWK canônico, assinatura raw
// r||s de 64 bytes) precisam casar exatamente com o que o backend valida —
// qualquer divergência faz a assinatura ser rejeitada silenciosamente.

export const base64UrlEncode = bytes => {
  let binary = '';
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < view.byteLength; i += 1) {
    binary += String.fromCharCode(view[i]);
  }
  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export const base64UrlDecode = value => {
  const pad = value.length % 4 === 0 ? 0 : 4 - (value.length % 4);
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(pad);
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// `extractable: false` faz a privada existir apenas como handle opaco no
// keystore do navegador — nem código legítimo nem XSS conseguem ler os bytes,
// só usá-la para assinar.
export const generateKeyPair = async () => {
  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify'],
  );
  const publicKeyJwk = await window.crypto.subtle.exportKey(
    'jwk',
    keyPair.publicKey,
  );
  return {
    privateKey: keyPair.privateKey,
    publicKey: keyPair.publicKey,
    publicKeyJwk: {
      kty: publicKeyJwk.kty,
      crv: publicKeyJwk.crv,
      x: publicKeyJwk.x,
      y: publicKeyJwk.y,
    },
  };
};

// Fingerprint estável da chave pública. É o que o backend usa para procurar
// se esta estação de trabalho já foi aprovada. A string canonicalizada (campos em
// ordem fixa, sem espaços) precisa bater byte a byte com a versão do backend.
export const computeThumbprint = async jwk => {
  const canonical = `{"crv":"${jwk.crv}","kty":"${jwk.kty}","x":"${jwk.x}","y":"${jwk.y}"}`;
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(canonical),
  );
  return base64UrlEncode(new Uint8Array(digest));
};

// Assina o nonce decodificado (os bytes, não a string base64url) e devolve
// a assinatura em base64url. Erro comum: assinar a string — o backend
// rejeita silenciosamente porque os bytes não batem.
export const signNonce = async (privateKey, nonceB64u) => {
  const nonceBytes = base64UrlDecode(nonceB64u);
  const signature = await window.crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    nonceBytes,
  );
  return base64UrlEncode(new Uint8Array(signature));
};
